/*
 * Copyright (c) 2016 Omnigon Communications, LLC. All rights reserved.
 *
 * This software is the confidential and proprietary information of Omnigon Communications, LLC
 * ("Confidential Information"). You shall not disclose such Confidential Information and shall access and use it only
 * in accordance with the terms of the license agreement you entered into with Omnigon Communications, LLC, its
 * subsidiaries, affiliates or authorized licensee. Unless required by applicable law or agreed to in writing, this
 * Confidential Information is provided on an "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either
 * express or implied. See the license agreement for the specific language governing permissions and limitations.
 */

import Component from 'can/component/component';
import FeedService from 'scripts/services/base-feed-service';
import holeManager from 'scripts/services/hole-manager';
import can from 'can';
import Swiper from 'swiper';
import dtm from 'scripts/services/dtm';
import _ from 'lodash';
import template from './shot-by-shot.stache';

const sourceUrl = `https://${window.globalSettings.iframeEnvironment ||
    'usgamensbp.smtinternetservices.com'}/stroketrails/index.html?bp={layoutType}&pid={playerId}&r={round}&h={hole}`;
const playerShotsUrl = window.globalSettings.feeds.smt.playerShotsUrl;
const postMessagesSource =
    `https://${window.globalSettings.iframeEnvironment}` ||
    'https://usgamensbp.smtinternetservices.com';

Component.extend({
    tag: 'leaderboard-shot-by-shot',
    template: template,
    viewModel: {
        define: {
            playerId: {
                type: 'string',
            },
            noShots: {
                type: 'htmlbool',
                value: false,
            },
            round: {
                type: 'number',
            },
            activeHoleIndex: {
                type: 'number',
                value: null,
            },
            activeShotIndex: {
                type: 'number',
                value: null,
            },
            roundsInfo: {
                value: null,
            },
            holeScrolled: {
                type: 'boolean',
                value: false,
            },
            holes: {
                value: [],
            },
            layoutType: {
                type: 'string',
            },
            holeId: {
                type: 'string',
                get() {
                    return holeManager.getNextHole(
                        this.attr('roundsInfo'),
                        this.attr('round'),
                    );
                },
            },
            src: {
                get() {
                    const round = this.attr('round') || 1;
                    const holeId = this.attr('activeHoleIndex') || 1;

                    return can.sub(sourceUrl, {
                        layoutType: this.attr('layoutType'),
                        playerId: this.attr('playerId'),
                        round: round,
                        hole: holeId,
                    });
                },
            },
            getActiveHole: {
                get() {
                    const holes = this.attr('holes').serialize();
                    //Possible fix for SEV-1, need to check
                    const activeHoleIndex = this.attr('activeHoleIndex');
                    return holes[activeHoleIndex];
                },
            },
            getHoles: {
                get() {
                    const holes = this.attr('holes');
                    const activeShotIndex = this.attr('activeShotIndex');
                    return _.map(holes, (hole) => ({
                        identifier: hole.identifier,
                        isActive: hole.identifier === activeShotIndex,
                        shots: hole.shots,
                    }));
                },
            },
        },
        expandShots: function(event, index) {
            this.attr('holes').each((hole, holeIndex) => {
                if (index == holeIndex) {
                    hole.attr('expanded', true);
                }
            });
        },
        onHolesSelectChange: function(event) {
            let slideIndex = $(event.target).val();
            this.attr('activeShotIndex', slideIndex);

            if (this.swiper && this.swiper.slideTo) {
                this.swiper.slideTo(slideIndex);
            }
        },
        setHoles: function(round) {
            let rounds = this.attr('rounds') || new can.List([]);
            let roundInfo = rounds.attr(round - 1);
            let holes = roundInfo && roundInfo.holes ? roundInfo.holes : [];

            const lastHoleWithResult = _.findLastIndex(
                holes,
                (hole) => hole.shots && hole.shots.length,
            );

            this.attr('holes', holes);

            if (!this.attr('holeScrolled')) {
                this.attr('activeHoleIndex', lastHoleWithResult);
                this.changeIframeSrc();
            }
        },
        changeIframeSrc(direction) {
            const round = this.attr('round') || 1;
            const holeResults = this.attr(
                `roundsInfo.${round - 1}.scorecard.holeResults`,
            );
            let index = this.attr('activeHoleIndex');
            const nextHole = holeManager.getNextHole(
                this.attr('roundsInfo'),
                this.attr('round'),
            );
            let holeId = holeResults[index]
                ? holeResults[index].hole.identifier
                : nextHole;
            if (direction == 'left') {
                if (holeResults[index - 1]) {
                    holeId = holeResults[index - 1].hole.identifier;
                    index = index - 1;
                } else {
                    holeId =
                        holeResults[holeResults.length - 1].hole.identifier;
                    index = holeResults.length - 1;
                }
            } else if (direction === 'right') {
                if (holeResults[index + 1]) {
                    holeId = holeResults[index + 1].hole.identifier;
                    index = index + 1;
                } else {
                    holeId = holeResults[0].hole.identifier;
                    index = 0;
                }
            }

            this.attr('activeHoleIndex', index);
            this.attr(
                'iframeSrc',
                can.sub(sourceUrl, {
                    layoutType: this.attr('layoutType'),
                    playerId: this.attr('playerId'),
                    round: round,
                    hole: holeId,
                }),
            );
        },

        initSwiper: function() {
            const swiperContainer = $('.s-shot-by-shot-text .swiper-container');
            const paginationEl = $('.s-shot-by-shot-text .swiper-pagination');
            const holesSelect = $('.s-shot-by-shot-text .s-holes-select');

            if (!swiperContainer.length) {
                return;
            }

            if (this.swiper) {
                this.destroySwiper(swiperContainer);
            }

            this.swiper = new Swiper(swiperContainer, {
                init: false,
                loop: false,
                simulateTouch: true,
                initialSlide: this.attr('activeShotIndex'),
                speed: 1000,
                observer: true,
                observeSlideChildren: true,
                observeParents: true,
                slidesPerColumn: 1,
                pagination: {
                    el: paginationEl,
                    clickable: true,
                },
            });

            this.swiper.on('slideChangeTransitionEnd', () => {
                this.attr('activeHoleIndex', this.swiper.activeIndex);
                holesSelect.val(this.swiper.activeIndex);
            });

            this.swiper.on('init', () =>
                holesSelect.val(this.swiper.activeIndex),
            );

            this.swiper.init();
        },
        destroySwiper: function(swiperContainer) {
            if (this.swiper && this.swiper.destroy) {
                this.swiper.destroy();
            }

            if (swiperContainer) {
                swiperContainer.removeAttr('style');
            }

            this.swiper = null;
        },
    },
    events: {
        init() {
            this.viewModel.bind('holes', () => this.viewModel.initSwiper());
            this.viewModel.changeIframeSrc();
            this.initShots();
        },

        inserted: function() {
            window.addEventListener('message', this.handlePostMessage, false);
            // this.viewModel.attr(
            //     'activeHoleIndex',
            //     this.viewModel.attr('holeId'),
            // );

            if (this.viewModel.attr('noShots') === false) {
                this.initShots();
            }

            this.viewModel.bind('playerId', () => {
                this.initShots();
            });
            this.initTextContainer();
        },

        initShots: function() {
            const playerFeedPath = can.sub(playerShotsUrl, {
                identifier: this.viewModel.attr('playerId'),
            });

            this.destroyFeedService();
            this.playerShotsService = new FeedService(
                playerFeedPath,
                false,
                true,
            )
                .on('data', (ev, data) => {
                    if (data.rounds && data.rounds.length) {
                        this.viewModel.attr(
                            'rounds',
                            data.rounds || new can.List([]),
                        );
                        let round = this.viewModel.attr('round') || 1;
                        this.viewModel.setHoles(round, true);
                    }
                })
                .on('error', (error) => {
                    console.error('Error fetching player shots data', error);
                });

            this.playerShotsService.fetch();
        },

        destroyFeedService: function() {
            if (this.playerShotsService) {
                this.playerShotsService.destroy();
                this.playerShotsService = null;
            }
        },

        handlePostMessage: function(event) {
            if (event && event.data && event.origin == postMessagesSource) {
                let data = event.data;
                if (data && data.PlayerId) {
                    dtm.trackShots(data.PlayerId);
                }
            }
        },

        removed: function() {
            window.removeEventListener(
                'message',
                this.handlePostMessage,
                false,
            );
            this.destroyFeedService();
        },

        initTextContainer: function() {
            const contentTextContainer = this.element.find(
                '.s-shot-by-shot-content-container__item--shots',
            );
            const contentTextContainerValue =
                contentTextContainer.outerWidth() -
                contentTextContainer.prop('clientWidth');
            const contentTextTableWrapper = contentTextContainer.find(
                '.s-shot-by-shot-table-wrapper',
            );

            if (contentTextContainerValue > 1) {
                contentTextContainer.css({
                    paddingRight: contentTextContainerValue,
                    right: -contentTextContainerValue,
                });
                contentTextTableWrapper.css({
                    width: `calc(100% + ${contentTextContainerValue}px)`,
                });
            }
        },

        '{scope} round'(scope, event, round) {
            this.viewModel.attr('iframeSrc', null);
        },

        '{scope} holeId'(scope, event, round) {
            this.viewModel.attr(
                'activeHoleIndex',
                this.viewModel.attr('holeId'),
            );
        },

        '.js-content-tab click'(element) {
            const tabs = element
                .closest('.s-shot-by-shot-content-tabs')
                .find('.s-shot-by-shot-content-tabs__item');
            const contentBlocks = element
                .closest('.s-shot-by-shot-content')
                .find('.s-shot-by-shot-content-container')
                .find('.s-shot-by-shot-content-container__item');
            const elementIndex = element.attr('data-index');
            contentBlocks.css({ 'z-index': 0 });
            $(contentBlocks[elementIndex - 1]).css({ 'z-index': 1 });
            tabs.removeClass('is-active');
            element.addClass('is-active');
            this.initShots();
        },
        '.s-shot-by-shot-content-tabs__item click'(element) {
            dtm.trackSwitchToShotsViewInDrawer();
        },

        '.s-shot-by-shot-content-container__transparent-button--left click'() {
            this.viewModel.attr('holeScrolled', true);
            this.viewModel.changeIframeSrc('left');
        },

        '.s-shot-by-shot-content-container__transparent-button--right click'() {
            this.viewModel.attr('holeScrolled', true);
            this.viewModel.changeIframeSrc('right');
        },
    },
});
